// Updated MemberCard.jsx with View Report support
import React, { useState, useEffect, useRef } from 'react';
import config from '../../../config';
import { toast } from '../../../components/Common/Toast';
import axios from 'axios';
import InterviewSetupModal from '../../../components/Teams/interview/InterviewSetupModal';
import MilitaryAssessmentSetupModal from '../../../components/Teams/assessment/MilitaryAssessmentSetupModal';
import DetailedAnalyticsModal from '../../../components/Teams/interview/DetailedAnalyticsModal';

const MemberCard = ({ 
  isAdmin, 
  user, 
  token, 
  member, 
  onRemove, 
  onViewDetails, 
  currentUserId, 
  onStartInterview, 
  teamId,
  teamType,
  onStartMilitarySession,
  militaryProgramStatus 
}) => {
  const isCurrentUserFriend = member.is_friend;
  const isCurrentUser = member.user_id === currentUserId;
  const [isSending, setIsSending] = useState(false);
  const [hasSentRequest, setHasSentRequest] = useState(member.is_friendRequestSent === 1);
  const [showOptions, setShowOptions] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showMilitaryModal, setShowMilitaryModal] = useState(false);
  const [isStartingInterview, setIsStartingInterview] = useState(false);
  const [memberProgramStatus, setMemberProgramStatus] = useState(null);
  
  // New state for View Report
  const [showReportModal, setShowReportModal] = useState(false);
  const [latestInterviewId, setLatestInterviewId] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  // Ref for dropdown menu to handle click outside
  const dropdownRef = useRef(null);

  const isMilitaryTeam = teamType === 'military';
  const canViewDetails = isCurrentUserFriend || isCurrentUser;

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  // Fetch member's military program status
  useEffect(() => {
    if (isMilitaryTeam && member.user_id && showOptions === true) {
      fetchMemberProgramStatus();
    }
  }, [isMilitaryTeam, member.user_id, showOptions]);

  const fetchMemberProgramStatus = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/api/military/user/${member.user_id}/program-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setMemberProgramStatus(response.data.program);
      }
    } catch (error) {
      console.error('Error fetching program status:', error);
    }
  };

  const handleAddFriend = async () => {
    setIsSending(true);
    try {
      const response = await axios.post(
        `${config.baseURL}/api/notifications/send`, 
        {
          receiver_user_id: member.user_id,
          receiver_profile_id: member.profileId,
          sender_user_id: user?.user_id || user?.id,
          sender_profile_id: user?.profileId,
          type: "connect",
          message: `${user?.first_name} wants to connect with you`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success("Connection request sent!");
        setHasSentRequest(true);
      }
    } catch (error) {
      console.error("Error sending request", error);
      toast.error("Failed to send request");
    } finally {
      setIsSending(false);
    }
  };

  const handleStartInterviewClick = () => {
    setShowInterviewModal(true);
    setShowOptions(false);
  };

  const handleStartMilitaryClick = () => {
    setShowMilitaryModal(true);
    setShowOptions(false);
  };

  const handleStartInterview = async (interviewConfig) => {
    if (!teamId) {
      toast.error('Please select a team first');
      return;
    }
    
    if (isStartingInterview) return;
    setIsStartingInterview(true);
    
    try {
      const interviewData = {
        ...interviewConfig,
        user_id: member.user_id,
        // Pass candidate as array of user_ids for consistency
        candidates: [member.user_id],
        candidate_names: [`${member.first_name} ${member.last_name}`]
      };
      
      await onStartInterview(interviewData);
      setShowInterviewModal(false);
      toast.success('Interview started successfully!');
    } catch (err) {
      console.error('Error starting interview:', err);
      toast.error(err.response?.data?.message || 'Failed to start interview');
    } finally {
      setIsStartingInterview(false);
    }
  };

  const handleStartMilitarySession = async (sessionConfig) => {
    if (!teamId) {
      toast.error('Please select a team first');
      return;
    }
    
    try {
      const sessionData = {
        ...sessionConfig,
        user_id: member.user_id,
        program_id: memberProgramStatus?.program_id
      };
      
      await onStartMilitarySession(sessionData);
      setShowMilitaryModal(false);
      toast.success(`Week ${sessionConfig.week_number} assessment started!`);
    } catch (err) {
      console.error('Error starting military session:', err);
      toast.error(err.response?.data?.message || 'Failed to start assessment');
    } finally {
      setIsStartingInterview(false);
    }
  };

  const getNextAvailableWeek = () => {
    if (!memberProgramStatus) return 1;
    return memberProgramStatus.current_week || 1;
  };

  // Function to fetch and show the report
  const handleViewReport = async () => {
    if (!member.user_id || !teamId) {
      toast.error('Cannot load report');
      return;
    }
    
    setIsLoadingReport(true);
    setShowOptions(false); // Close dropdown
    
    try {
      // Fetch latest completed interview for this member in this team
      const { data } = await axios.get(
        `${config.baseURL}/api/teams/interview/latest/${member.user_id}`,
        { 
          params: { team_id: teamId },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      if (data.success && data.interview_id) {
        setLatestInterviewId(data.interview_id);
        setShowReportModal(true);
      } else {
        toast.info('No completed interview report found for this member');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    } finally {
      setIsLoadingReport(false);
    }
  };

  return (
    <>
      <div className="member-card">
        <div className="member-header">
          <div className="member-avatar">
            <img 
              src={member.profile_image ? `${config.baseURL}/uploads/profiles/${member.profile_image}` : '/images/no-image-found.webp'} 
              alt={`${member.first_name} ${member.last_name}`}
              onError={(e) => { e.target.src = '/images/no-image-found.webp'; }}
            />
            {isMilitaryTeam && memberProgramStatus && (
              <div className={`military-status-badge status-${memberProgramStatus.status}`}>
                W{memberProgramStatus.current_week}
              </div>
            )}
          </div>
          <div className="member-basic-info">
            <h4 className="member-name">{member.first_name} {member.last_name}</h4>
            <p className="member-id">ID: {member.profileId}</p>
            <p className="member-profession">
              {member.profession ? (member.profession.length > 20 ? `${member.profession.slice(0, 20)}...` : member.profession) : 'No profession'}
            </p>
            {isMilitaryTeam && memberProgramStatus && (
              <div className="military-progress-mini">
                <div className="progress-bar-mini">
                  <div 
                    className="progress-fill-mini" 
                    style={{ width: `${(memberProgramStatus.current_week / 6) * 100}%` }}
                  />
                </div>
                <span className="progress-text">Week {memberProgramStatus.current_week}/6</span>
              </div>
            )}
          </div>
          
          {!member.is_team_creator && isAdmin && (
            <div className="team-actions" ref={dropdownRef}>
              <button 
                className="team-options-btn" 
                onClick={() => setShowOptions(!showOptions)}
                disabled={isStartingInterview}
              >
                ⋮
              </button>
              {showOptions && (
                <div className="team-dropdown">
                  {isMilitaryTeam ? (
                    <>
                      {memberProgramStatus?.status === 'active' && (
                        <button 
                          className="dropdown-item" 
                          onClick={handleStartMilitaryClick}
                        >
                          Start Week {getNextAvailableWeek()} Assessment
                        </button>
                      )}
                      {!memberProgramStatus && (
                        <button 
                          className="dropdown-item" 
                          onClick={handleStartMilitaryClick}
                        >
                          Start Military Program
                        </button>
                      )}
                      <button 
                        className="dropdown-item" 
                        onClick={handleStartInterviewClick}
                      >
                        Quick Interview
                      </button>
                    </>
                  ) : (
                    <button 
                      className="dropdown-item" 
                      onClick={handleStartInterviewClick}
                    >
                      Start Interview
                    </button>
                  )}
                  {/* Add View Report button in dropdown for admin */}
                  <button 
                    className="dropdown-item" 
                    onClick={handleViewReport}
                    disabled={isLoadingReport}
                  >
                    {isLoadingReport ? 'Loading...' : 'View Report'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="member-actions">
          {canViewDetails ? (
            <button className="view-details-btn" onClick={() => onViewDetails(member)}>
              View Details
            </button>
          ) : (
            <button 
              className="view-details-btn"
              onClick={handleAddFriend}
              disabled={isSending || hasSentRequest}
            >
              {isSending ? 'Sending...' : hasSentRequest ? 'Request Sent' : 'Add Friend'}
            </button>
          )}
          
          {member?.is_team_creator ? null : isAdmin ? (
            <button 
              className="remove-btn"
              onClick={() => onRemove(member.team_member_id)}
              disabled={isCurrentUser}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>

      {/* Fixed: Pass selectedFriends as an array instead of selectedFriend as object */}
      <InterviewSetupModal 
        show={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        onStart={handleStartInterview}
        teamName={member.team_name || 'Team'}
        selectedFriends={[member]}
      />

      <MilitaryAssessmentSetupModal 
        show={showMilitaryModal}
        onClose={() => setShowMilitaryModal(false)}
        onStart={handleStartMilitarySession}
        teamName={member.team_name || 'Team'}
        selectedMembers={[member]}
        weekNumber={getNextAvailableWeek()}
      />

      {/* Report Modal for admin */}
      {showReportModal && latestInterviewId && (
        <DetailedAnalyticsModal
          interviewId={latestInterviewId}
          token={token}
          candidateId={member.user_id}
          onClose={() => {
            setShowReportModal(false);
            setLatestInterviewId(null);
          }}
        />
      )}
    </>
  );
};

export default MemberCard;